import { React, useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import UserSelectors from 'Stores/User/Selectors'
import AuthSelectors from 'Stores/Auth/Selectors'
import UserActions from 'Stores/User/Actions'
import styles from './TransactionList.module.css'
import RequestState from 'Enums/RequestState'
import {
  TransactionSessionDetail,
  FundTransactionSessionDetail,
  BalanceTimeline,
  IFSkeleton,
  IFText,
  IFTable,
  IFButton,
} from 'Components'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Colors } from 'Theme'
import TransactionTimeline from '../TransactionTimeline/TransactionTimeline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Skeleton } from '@mui/material'
import ReportIcon from '@mui/icons-material/Report'
import TableRowsIcon from '@mui/icons-material/TableRows'
import TimelineIcon from '@mui/icons-material/Timeline'

const TransactionList = ({
  userId,
  setCurrentUserTransactionRefund,
  transactions,
  transactionsFilters,
  fetchUserTransactionList,
  transactionListPaginationOffset,
  fetchUserTransactionListRequestState,
  editingAdminRole,
  fetchUserListRequestState,
  tablesHeight,
  selectedUserId,
  loadHeaders,
  selectedUserIndex,
  userDetailsRequestState,
  pointsTariff,
}) => {
  const { t } = useTranslation()
  // 'table' is the familiar per-transaction grid; 'timeline' reads the same page
  // of transactions as a balance trail, for walking a customer through a dispute.
  const [view, setView] = useState('table')
  const [
    fetchTransactionListLoadingState,
    setFetchTransactionListLoadingState,
  ] = useState(true)
  useEffect(() => {
    if (selectedUserId) setFetchTransactionListLoadingState(true)
    setView('table')
  }, [selectedUserId])
  useEffect(() => {
    if (fetchUserTransactionListRequestState === RequestState.SUCCEEDED)
      setFetchTransactionListLoadingState(false)
  }, [fetchUserTransactionListRequestState])
  const loadMoreData = () => {
    if (
      fetchUserTransactionListRequestState === RequestState.LOADING ||
      transactionListPaginationOffset === null
    )
      return
    fetchUserTransactionList(
      selectedUserId,
      transactionsFilters,
      transactionListPaginationOffset,
      failedRequest,
    )
  }

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchUserTransactionListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchUserTransactionListRequestState ===
        RequestState.ERROR_400_OCCURRED ||
      fetchUserTransactionListRequestState ===
        RequestState.ERROR_401_OCCURRED ||
      fetchUserTransactionListRequestState ===
        RequestState.ERROR_403_OCCURRED ||
      fetchUserTransactionListRequestState ===
        RequestState.ERROR_409_OCCURRED ||
      fetchUserTransactionListRequestState ===
        RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchUserTransactionListRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchUserTransactionListRequestState])
  const handleRetry = () => {
    fetchUserTransactionList(
      userId,
      transactionsFilters,
      transactionListPaginationOffset,
    )
  }

  // The table headers, the view toggle and the table body all skeleton on the
  // same signal, so the whole panel resolves in one step instead of the toggle
  // appearing on its own ahead of everything else.
  const headersLoading =
    fetchUserListRequestState === RequestState.LOADING ||
    (fetchUserTransactionListRequestState === RequestState.LOADING &&
      fetchTransactionListLoadingState) ||
    loadHeaders ||
    selectedUserIndex === -1

  const transactionTableColumns = useMemo(
    () => [
      {
        accessorKey: 'type',
        size: 120,
        header:
          headersLoading ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.TransactionTableColumns.type')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length &&
            transactions &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <div className={styles.ColumnTitle}>
              <ExpandMoreIcon
                className={styles.ExpandMoreIcon}
                style={{
                  transform: row.getIsExpanded()
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)',
                }}
              />
              <div className={styles.TransactionName}>
                <IFText>
                  {t(
                    `UserPage.TransactionTableColumns.Type.${row.original.type}`,
                  )}
                </IFText>
              </div>
            </div>
          ) : failedRequest ? (
            <div className={styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'transactionID',
        size: 180,
        header:
          headersLoading ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.TransactionTableColumns.transactionID')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length &&
            transactions &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{row.original._id}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'name',
        size: 120,
        header:
          headersLoading ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.TransactionTableColumns.Name')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length &&
            transactions &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>
              {row.original.type === 'charge'
                ? row.original.details?.cdrLocation?.name || '-'
                : row.original.paymentIssuer}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'amountText',
        header:
          headersLoading ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.TransactionTableColumns.Amount')
          ),
        Cell: ({ row }) => {
          const amountExists =
            row.original.amount === 0 || row.original.amount === '0'
              ? false
              : true
          const pointsExists =
            row.original.pointsAmount === 0 ||
            row.original.pointsAmount === '0' ||
            row.original.pointsAmount === undefined
              ? false
              : true
          const amountText = row.original.isCredit
            ? `+${row.original.country?.currency?.code} ${Number(
                row.original.amount,
              ).toFixed(row.original.country?.currency?.decimalPlaces || 2)}`
            : `-${row.original.country?.currency?.code} ${Number(
                row.original.amount,
              ).toFixed(row.original.country?.currency?.decimalPlaces || 2)}`
          const pointsAmount = row.original.isCredit
            ? `+PTS ${Intl.NumberFormat('en-US').format(
                row.original.pointsAmount,
              )}`
            : `-PTS ${Intl.NumberFormat('en-US').format(
                row.original.pointsAmount,
              )}`
          let amount = ''
          if (row.original.type === 'charge') {
            amount =
              amountExists && pointsExists
                ? `${amountText}/${pointsAmount}`
                : amountExists
                ? amountText
                : pointsExists
                ? pointsAmount
                : amountText
          } else {
            amount = amountText
          }
          return row.index < transactions.length &&
            transactions &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{amount}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header:
          headersLoading ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.TransactionTableColumns.ProcessedOn')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length &&
            transactions &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>
              {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
            </IFText>
          ) : failedRequest ? (
            <div className={styles.IconDiv}>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={() => {
                  handleRetry()
                }}
              />
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      transactions,
      headersLoading,
      fetchUserTransactionListRequestState,
      userDetailsRequestState,
      failedRequest,
    ],
  )

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )
  const renderDetailPanel = ({ row }) =>
    row.original.type === 'charge' ? (
      <TransactionSessionDetail
        row={row}
        pointsTariff={pointsTariff}
        timelineSlot={<TransactionTimeline transaction={row.original} />}
      />
    ) : (
      <FundTransactionSessionDetail
        row={row}
        editingAdminRole={editingAdminRole}
        onRefundClick={() => {
          setCurrentUserTransactionRefund(row.original._id, row.index)
        }}
        isRefundDisabled={
          row.original.isRefunded ||
          moment(new Date().setHours(0, 0, 0, 0)).diff(row.original.createdAt) /
            86400000 >
            14
        }
      />
    )

  const isInitialLoad =
    (fetchUserTransactionListRequestState === RequestState.LOADING &&
      transactionListPaginationOffset === 0) ||
    (fetchUserTransactionListRequestState !== RequestState.SUCCEEDED &&
      !transactionListPaginationOffset) ||
    selectedUserIndex === -1

  return (
    <div className={styles.tables}>
      <div className={styles.ViewToggle}>
        {[
          { key: 'table', Icon: TableRowsIcon, label: t('UserPage.ViewTable') },
          {
            key: 'timeline',
            Icon: TimelineIcon,
            label: t('UserPage.ViewBalanceTimeline'),
          },
        ].map(({ key, Icon, label }) =>
          headersLoading ? (
            <IFSkeleton
              key={key}
              variant="rounded"
              width={key === 'table' ? '76px' : '142px'}
              height="26px"
              className={styles.ViewToggleSkeleton}
            />
          ) : (
            <div
              key={key}
              className={styles.ViewToggleOption}
              style={{
                backgroundColor: view === key ? Colors.primary : Colors.white,
                color: view === key ? Colors.white : Colors.darkGrey,
              }}
              onClick={() => setView(key)}
            >
              <Icon sx={{ fontSize: '16px' }} />
              <IFText className={styles.ViewToggleText}>{label}</IFText>
            </div>
          ),
        )}
      </div>

      {view === 'timeline' ? (
        <BalanceTimeline
          transactions={transactions}
          isLoading={isInitialLoad}
          hasMore={transactionListPaginationOffset !== null}
          onLoadMore={loadMoreData}
          maxHeight={tablesHeight - 44}
        />
      ) : (
        <IFTable
          columns={transactionTableColumns}
          data={transactions}
          fetchMoreOnBottomReached={fetchMoreOnBottomReached}
          paginationOffset={transactionListPaginationOffset}
          filters={transactionsFilters}
          showSkeleton={isInitialLoad}
          renderDetailPanel={renderDetailPanel}
          tableMaxHeight={tablesHeight - 44}
          tableSetHeight={'100%'}
          tableRequestState={fetchUserTransactionListRequestState}
        />
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    setCurrentUserTransactionRefund: (id, index) =>
      dispatch(UserActions.setCurrentUserTransactionRefund(id, index)),
    fetchUserTransactionList: (userId, filter, offset, shouldShowError) =>
      dispatch(
        UserActions.fetchUserTransactionList(
          userId,
          filter,
          offset,
          shouldShowError,
        ),
      ),
  }
}

const mapStateToProps = (state) => ({
  selectedUserId: UserSelectors.getSelectedUserId(state),
  selectedUserIndex: UserSelectors.getSelectedUserIndex(state),
  userDetailsRequestState: UserSelectors.getUserDetailsRequestState(state),
  transactions: UserSelectors.getTransactionList(state),
  transactionListPaginationOffset:
    UserSelectors.getTransactionListPaginationOffset(state),
  fetchUserTransactionListRequestState:
    UserSelectors.getFetchUserTransactionListRequestState(state),
  transactionsFilters: UserSelectors.getTransactionsFilters(state),
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  fetchUserListRequestState: UserSelectors.getFetchUserListRequestState(state),
  pointsTariff: UserSelectors.getPointsTariff(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(TransactionList)
